import React, { createContext, ReactNode, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { Profile, profileService } from 'src/services/profileService';
import { getCurrentUser, onAuthStateChange } from 'src/utils/supabase';

interface ProfileContextType {
  profile: Profile | null;
  loading: boolean;
  error: string | null;
  fetchProfile: () => Promise<void>;
  updateProfile: (updates: Partial<Profile>) => Promise<boolean>;
  clearError: () => void;
}

const ProfileContext = createContext<ProfileContextType | undefined>(undefined);

export const useProfile = () => {
  const context = useContext(ProfileContext);
  if (context === undefined) {
    throw new Error('useProfile must be used within a ProfileProvider');
  }
  return context;
};

interface ProfileProviderProps {
  children: ReactNode;
}

export const ProfileProvider: React.FC<ProfileProviderProps> = ({ children }) => {
  const [profile, setProfile] = useState<Profile | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchProfile = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const user = await getCurrentUser();
      if (!user) {
        setError('No authenticated user found');
        return;
      }

      const profileData = await profileService.getProfile(user.id);
      setProfile(profileData);
    } catch {
      setError('Failed to fetch profile');
    } finally {
      setLoading(false);
    }
  }, []);

  const updateProfile = useCallback(
    async (updates: Partial<Profile>): Promise<boolean> => {
      if (!profile?.id) return false;

      setLoading(true);
      setError(null);

      try {
        const success = await profileService.updateProfile(profile.id, updates);
        if (success) {
          setProfile((prev) => (prev ? { ...prev, ...updates } : null));
        }
        return success;
      } catch {
        setError('Failed to update profile');
        return false;
      } finally {
        setLoading(false);
      }
    },
    [profile?.id]
  );

  const clearError = useCallback(() => {
    setError(null);
  }, []);

  // Fetch profile immediately on mount AND subscribe to auth state changes.
  // The immediate fetch handles the steady-state "page reload while logged in"
  // case without depending on the listener firing. The listener catches
  // sign-in / sign-out / user-update events that happen after mount.
  useEffect(() => {
    let cancelled = false;

    fetchProfile();

    const unsubscribe = onAuthStateChange((event, session) => {
      if (cancelled) return;
      if (event === 'SIGNED_OUT') {
        setProfile(null);
        setError(null);
        setLoading(false);
        return;
      }
      if (event === 'SIGNED_IN' || event === 'USER_UPDATED') {
        if (session?.user) fetchProfile();
      }
    });

    return () => {
      cancelled = true;
      unsubscribe();
    };
  }, [fetchProfile]);

  const value: ProfileContextType = useMemo(
    () => ({
      profile,
      loading,
      error,
      fetchProfile,
      updateProfile,
      clearError,
    }),
    [profile, loading, error, fetchProfile, updateProfile, clearError]
  );

  return <ProfileContext.Provider value={value}>{children}</ProfileContext.Provider>;
};
